import { DotsThree, PencilSimpleLine, Trash } from "@phosphor-icons/react";
import clsx from "clsx";
import { useState } from "react";

import EmojiSelector from "common/components/EmojiSelector";
import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import { useActiveConversationContext } from "../../../context/ActiveConversationContext";
import useConversationMessagesUpdates from "../../../datahooks/useConversationMessagesUpdates";
import useSNConversation from "../../../hooks/useSNConversation";
import MessageMenu from "./MessageMenu";

import type { ConversationMessage } from "../../../types";

import SmileyAdd from "assets/icons/smiley-add.svg?react";

interface EmojiReactionsProps {
  senderId: string;
  message: ConversationMessage;
  setEditMessage: (message: ConversationMessage) => void;
  isMenuVisible: boolean;
}

const defaultEmojiReactions = [
  {
    emoji: "👍",
  },
  {
    emoji: "👏",
  },
  {
    emoji: "😊",
  },
];

export default function EmojiReactions({
  senderId,
  message,
  isMenuVisible,
  setEditMessage,
}: EmojiReactionsProps) {
  const isDesktop = useTwBreakpoint("lg");
  const { activeConversation } = useActiveConversationContext();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { isSNConversation } = useSNConversation(activeConversation);
  const { conversation_urn } = activeConversation;
  const { sendMessageReaction } = useConversationMessagesUpdates({
    conversationUrn: conversation_urn,
  });

  const { deleteMessage } = useConversationMessagesUpdates({
    conversationUrn: conversation_urn,
  });

  function onMessageReact(emoji: string) {
    // whether the user reacted or not
    const { reader_reacted: isReacted } =
      message.reactions?.find((reaction) => reaction.emoji === emoji) || {};

    sendMessageReaction({
      accountId: senderId,
      emoji,
      messageUrn: message.urn,
      unreact: isReacted,
    });
  }

  if (!isDesktop)
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button intent="iconOnly" size="xs" variant="ghost-black">
            <DotsThree weight="bold" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent>
          <DropdownMenuList>
            <div
              className={clsx([
                "flex items-center justify-start gap-3 px-3 py-1",
                isMenuVisible && "mb-1.5 border-b border-black-200",
              ])}
            >
              {defaultEmojiReactions.map((emoji) => {
                const { emoji: emojiValue } = emoji;

                return (
                  <Button
                    key={emojiValue}
                    size="xs"
                    variant="ghost-black"
                    intent="iconOnly"
                    onClick={() => onMessageReact(emojiValue)}
                  >
                    {emojiValue}
                  </Button>
                );
              })}

              <EmojiSelector
                buttonProps={{ children: <SmileyAdd />, size: "xs" }}
                onEmojiSelection={onMessageReact}
              />
            </div>

            <RenderIf condition={isMenuVisible}>
              <RenderIf condition={!!message.body}>
                <DropdownMenuItem onClick={() => setEditMessage(message)}>
                  <PencilSimpleLine />
                  Edit message
                </DropdownMenuItem>
              </RenderIf>

              <DropdownMenuItem
                variant="danger"
                onClick={() =>
                  deleteMessage({
                    accountId: senderId,
                    messageUrn: message.urn,
                  })
                }
              >
                <Trash />
                Delete message
              </DropdownMenuItem>
            </RenderIf>
          </DropdownMenuList>
        </DropdownMenuContent>
      </DropdownMenu>
    );

  return (
    <div
      className={clsx(
        "my-auto flex-row items-center justify-center gap-1.5 rounded-full border border-black-200 bg-white flex opacity-0 pointer-events-none",
        !message.deleted &&
          "group-hover:opacity-100 has-[*[data-state=open]]:opacity-100 group-hover:pointer-events-auto",
      )}
    >
      <RenderIf condition={!isSNConversation}>
        {defaultEmojiReactions.map((emoji) => {
          const { emoji: emojiValue } = emoji;

          return (
            <Button
              key={emojiValue}
              size="sm"
              variant="ghost-black"
              intent="iconOnly"
              onClick={() => onMessageReact(emojiValue)}
            >
              <span className="text-sm">{emojiValue}</span>
            </Button>
          );
        })}
      </RenderIf>

      {isSNConversation ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost-black" intent="iconOnly" size="sm" disabled>
              <SmileyAdd />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            Reactions aren’t supported in Sales Navigator conversations.
          </TooltipContent>
        </Tooltip>
      ) : (
        <EmojiSelector
          buttonProps={{ size: "sm", children: <SmileyAdd /> }}
          onEmojiSelection={onMessageReact}
        />
      )}

      <RenderIf condition={isMenuVisible}>
        <MessageMenu
          isOpen={isDropdownOpen}
          setIsOpen={setIsDropdownOpen}
          accountId={senderId}
          message={message}
          setEditMessage={setEditMessage}
        />
      </RenderIf>
    </div>
  );
}
